import { IonButton } from '@ionic/react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BSegment } from '../../../../components/molecules/BSegment';
import { BSelectSearch } from '../../../../components/molecules/BSelectSearch';
import { BToggle } from '../../../../components/molecules/BToggle';
import { DocDate } from '../../../../components/molecules/docForm/DocDate';
import { getCurrentYear, getLastDayOfYearMinus4HoursUtc, getYearOfDate } from '../../../../config/utils/dateHelpers';
import { SUBSCRIPTION } from '../../../../interfaces/enumerables';
import {
  MONEY_COLLECTION_TRADER_TYPE,
  MONEY_COLLECTION_TRADER_TYPE_LABEL,
} from '../../../../interfaces/moneyCollection.interface';
import { usePlanIsActive } from '../../../../services/utils/usePlanIsActive';

interface Props {
  formik: any;
  customerField: any;
  includeMoneyCollectionField: any;
  startDateField: any;
  endDateField: any;
  includeDeliveryNotesField: any;
  traderTypeField: any;
}

export const CustomerReportFilters = ({
  formik,
  customerField,
  includeMoneyCollectionField,
  startDateField,
  endDateField,
  includeDeliveryNotesField,
  traderTypeField,
}: Props) => {
  const { t } = useTranslation();
  const currentYear = useMemo(() => getCurrentYear(), []);
  const showSubtotals = usePlanIsActive(SUBSCRIPTION.SUBTOTALS);

  const startDateValue = useMemo(() => formik.values?.[startDateField?.name], [formik.values, startDateField?.name]);
  const startDateYear = useMemo(() => getYearOfDate(startDateValue), [startDateValue]);
  const endDateValue = useMemo(() => formik.values?.[endDateField?.name], [endDateField?.name, formik.values]);
  const endDateYear = useMemo(() => getYearOfDate(endDateValue), [endDateValue]);

  const maxDateOfEndDate = useMemo(() => {
    const lastDayOfYear = getLastDayOfYearMinus4HoursUtc(startDateYear);
    if (startDateYear < currentYear) return dayjs(lastDayOfYear);

    return dayjs();
  }, [currentYear, startDateYear]);

  useEffect(() => {
    if (startDateYear && startDateYear !== endDateYear) {
      formik.setFieldValue(endDateField?.name, maxDateOfEndDate.toISOString());
    }
  }, [endDateField?.name, endDateYear, formik, maxDateOfEndDate, startDateValue, startDateYear]);

  const getMoneyCollectionToggle = useCallback(() => {
    if (showSubtotals) {
      return <BToggle field={includeMoneyCollectionField} formik={formik} />;
    }
  }, [formik, includeMoneyCollectionField, showSubtotals]);

  const isApplyDisabled = useMemo(() => {
    return !formik.isValid || !formik.dirty;
  }, [formik.dirty, formik.isValid]);

  return (
    <div className="flex-center-center-column-nowrap">
      <form onSubmit={formik.handleSubmit}>
        <div className="padding-t-0 padding-b-24 padding-l-8 padding-r-8 flex-center-center-row-wrap gap-10">
          <BSelectSearch field={customerField} useHook={customerField.useHook} formik={formik} focusOnMount />
          <DocDate field={startDateField} formik={formik} title={`${t('FORMS.START_DATE')} *`} maxDate={dayjs()} />
          <DocDate
            field={endDateField}
            formik={formik}
            title={`${t('FORMS.END_DATE')} *`}
            minDate={startDateValue}
            maxDate={maxDateOfEndDate}
          />
          {getMoneyCollectionToggle()}
          <BToggle field={includeDeliveryNotesField} formik={formik} />
          <BSegment
            field={traderTypeField}
            formik={formik}
            value1={MONEY_COLLECTION_TRADER_TYPE.CUSTOMER}
            value2={MONEY_COLLECTION_TRADER_TYPE.SUPPLIER}
            label1={MONEY_COLLECTION_TRADER_TYPE_LABEL[MONEY_COLLECTION_TRADER_TYPE.CUSTOMER]}
            label2={MONEY_COLLECTION_TRADER_TYPE_LABEL[MONEY_COLLECTION_TRADER_TYPE.SUPPLIER]}
          />
        </div>
        <div className="flex-center-center-row-wrap padding-t-10">
          <IonButton mode="md" type="submit" disabled={isApplyDisabled}>
            {t('FORMS.APPLY')}
          </IonButton>
        </div>
      </form>
    </div>
  );
};
